import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const MonthlyReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!startDate || !endDate) {
        setError('Please select both start date and end date');
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before or equal to end date');
        return;
      }
      
      const response = await api.get(`/admin/reports/monthly?startDate=${startDate}&endDate=${endDate}`);
      setReportData(response.data.data || []);
    } catch (err) {
      console.error('Error fetching monthly arrears report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load monthly arrears report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!startDate || !endDate) {
        setError('Please select both start date and end date');
        return;
      }
      
      if (new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before or equal to end date');
        return;
      }
      
      const response = await api.get(`/admin/reports/monthly/pdf?startDate=${startDate}&endDate=${endDate}`, {
        responseType: 'blob',
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateRangeStr = startDate === endDate 
        ? startDate 
        : `${startDate}_to_${endDate}`;
      link.download = `Monthly_Arrears_Report_${dateRangeStr}.pdf`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(err.response?.data?.error || err.message || 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals
  const totals = reportData.reduce(
    (acc, row) => ({
      totalArrearsWithoutInterest: acc.totalArrearsWithoutInterest + (Number(row.totalArrearsWithoutInterest) || 0),
      totalInterest: acc.totalInterest + (Number(row.totalInterest) || 0),
      totalArrearsWithInterest: acc.totalArrearsWithInterest + (Number(row.totalArrearsWithInterest) || 0),
    }),
    { totalArrearsWithoutInterest: 0, totalInterest: 0, totalArrearsWithInterest: 0 }
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Monthly Arrears Report
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PdfIcon />}
          onClick={downloadPdf}
          disabled={loading || reportData.length === 0}
          sx={{ ml: 2 }}
        >
          Download PDF
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: endDate }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={fetchReport}
                disabled={loading || !startDate || !endDate}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && reportData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : reportData.length > 0 ? (
        <>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Monthly Arrears Report
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Period: {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()} • Total Loans: {reportData.length}
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    Loan Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    Customer Details
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                    Total Arrears<br />(Without Interest)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                    Total Interest
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                    Total Arrears<br />(With Interest)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right' }}>
                    Arrears Age
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row, index) => (
                  <TableRow key={row.loanId || index} hover>
                    <TableCell>{row.loanNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.customerName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {row.customerAddress}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {row.customerContactNumber}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.totalArrearsWithoutInterest)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.totalInterest)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(row.totalArrearsWithInterest)}
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.arrearsAge).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                    TOTAL
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totals.totalArrearsWithoutInterest)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totals.totalInterest)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(totals.totalArrearsWithInterest)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Alert severity="info">
          Select a date range (From Date and To Date), then click "Generate Report" to view the arrears report.
        </Alert>
      )}
    </Box>
  );
};

export default MonthlyReport;

